import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  SettingHeader,
  SettingsRow,
  VStack,
} from "../../components/utils";
import {
  useGetReportSettings,
  useSetReportSettings,
} from "../../queries/siteSettings";
import { ReportSettings as ReportSettingsValues } from "../../types/SiteSettings";

const DEFAULT_SETTINGS: ReportSettingsValues = {
  maskPiiByDefault: false,
  excelPasswordProtectionEnabled: false,
  includeGlossaryByDefault: true,
  reportHistoryRetentionDays: 30,
};

function ReportSettings({ openCategory }: { openCategory?: string }) {
  const { data, isLoading } = useGetReportSettings();
  const { mutate: saveSettings, isLoading: isSaving } = useSetReportSettings();
  const [values, setValues] = useState<ReportSettingsValues>(DEFAULT_SETTINGS);

  useEffect(() => {
    if (data) setValues({ ...DEFAULT_SETTINGS, ...data });
  }, [data]);

  const isDirty = Object.keys(values).some(
    (key) =>
      values[key as keyof ReportSettingsValues] !==
      (data?.[key as keyof ReportSettingsValues] ??
        DEFAULT_SETTINGS[key as keyof ReportSettingsValues])
  );

  const update = (key: keyof ReportSettingsValues) => {
    setValues((current) => ({ ...current, [key]: !current[key] }));
  };

  const save = () => {
    saveSettings(values, {
      onSuccess: () => {
        toast.success("Report settings saved");
      },
      onError: () => {
        toast.error("We couldn't save report settings");
      },
    });
  };

  return (
    <Box className="w-full max-w-4xl p-6 bg-white rounded-lg">
      <VStack className="gap-6">
        <SettingHeader
          title="Report settings"
          description="Set company-wide defaults for report privacy and Excel exports."
        />
        <Box className="w-full border-b border-borderColor" />
        <VStack className="gap-5">
          <SettingsRow
            id="report-mask-pii"
            name="maskPiiByDefault"
            label="Mask personally identifiable information by default"
            description="New reports hide personal information unless an authorized user chooses to include it."
            type="toggle"
            value={values.maskPiiByDefault}
            onToggle={() => update("maskPiiByDefault")}
            disabled={isLoading || isSaving}
          />
          <SettingsRow
            id="report-history-retention"
            name="reportHistoryRetentionDays"
            label="Report history retention"
            description="Generated report files are automatically removed from S3 after this period. Audit history is retained."
            type="select"
            value={values.reportHistoryRetentionDays}
            options={[7, 30, 60, 90, 180].map((days) => ({
              label: `${days} days`,
              value: days,
            }))}
            textGetter={(option: { label: string }) => option.label}
            valueGetter={(option: { value: number }) => option.value}
            onChange={([days]) =>
              setValues((current) => ({
                ...current,
                reportHistoryRetentionDays: Number(days),
              }))
            }
            disabled={isLoading || isSaving}
          />
          <SettingsRow
            id="report-excel-password-protection"
            name="excelPasswordProtectionEnabled"
            label="Password-protect Excel reports"
            description="Exports are encrypted. The password is the first four characters of the requester's display name followed by the last four characters of the company CIN."
            type="toggle"
            value={values.excelPasswordProtectionEnabled}
            onToggle={() => update("excelPasswordProtectionEnabled")}
            disabled={isLoading || isSaving}
          />
          <SettingsRow
            id="report-include-glossary"
            name="includeGlossaryByDefault"
            label="Include a glossary when available"
            description="Adds definitions for report fields in exports that support a glossary sheet."
            type="toggle"
            value={values.includeGlossaryByDefault}
            onToggle={() => update("includeGlossaryByDefault")}
            disabled={isLoading || isSaving}
          />
        </VStack>
        <HStack className="justify-end gap-3 pt-4 border-t border-borderColor">
          <ButtonPrimary1
            type="button"
            disabled={!isDirty || isSaving}
            onClick={() => setValues({ ...DEFAULT_SETTINGS, ...(data ?? {}) })}
          >
            Cancel
          </ButtonPrimary1>
          <ButtonPrimary
            type="button"
            disabled={!isDirty || isSaving}
            onClick={save}
          >
            {isSaving ? "Saving changes" : "Save changes"}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </Box>
  );
}

export default ReportSettings;
